import { Fragment, useEffect, useState } from 'react'
import { Betrag } from './Betrag'
import { api, betrag, periodenkuerzel } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { useDarf } from './Rechte'
import { Auswahlfeld } from './Auswahlfeld'
import { artfelder, artwerte, Bewegungsartenwahl, quellfelder, quellwert, referenzfehler, useReferenzlisten } from './Kfreigenschaften'
import { ausnahmekonten, gegenproben, gegenprobenrest } from './Kfrausnahmen'
import { anzahlwort, beschriftung, bewegungsartname, kontobezeichnung, kurzform, satz, t } from './sprache'
import type { Sprachschluessel } from './sprache'
import { moeglicheAchsen, vorgabeFuer, weitereVon } from './Aufriss'
import type { Zelle } from './Aufriss'
import {
  Aufrisszeilen,
  ebenenschluessel,
  menueort,
  menuepunkte,
  Pfeilplatz,
  zeilenpfeilBetaetigen,
  zellkontext,
  zusatzachsen,
} from './Aufrisszeilen'
import type { Achsenquelle, Menueruf, Zellkontext } from './Aufrisszeilen'
import { einzug, istAufreissbar, istTrefferzelle, pfadVon, trefferklasse, Zeilenkopf } from './Bericht'
import { ACHSE_GUV, Ergebnisgliederung, useErgebnisgliederung, useGuvJeSpalte } from './Ergebnisgliederung'
import { useGliederungsbild } from './Gliederung'
import { nachStruktur } from './BerichtStruktur'
import type { Aufrissbedienung, Trefferzustand } from './Bericht'
import { zeilenId } from './Suche'
import {
  Spaltenleiste,
  Spaltenmassleiste,
  useSpaltenbreiten,
  useSpaltenmasse,
} from './Spaltenbreiten'
import { Zeilenmenue } from './Zeilenmenue'
import { Eigenschaftsfenster, zielVon } from './Eigenschaftsfenster'
import type { Eigenschaftsziel } from './Eigenschaftsfenster'
import { Zustandssymbol } from './Zustand'
import { useAnzeigeoptionen } from './Anzeigeoptionen'
import { spaltenfolge, verdichte, zwischenkopf, zwischenspalten } from './Zwischensummen'
import type {
  Berichtszeile,
  Kapitalflussrechnung as Kfrsatz,
  Abstimmgrund,
  Kfrabstimmzeile,
  Kfrbeitrag,
  Kfrformat,
  Kfrperiodenaufriss,
  Kfrperiodenbericht,
  Kfrperiodenzeile,
  Kfrreferenzkopf,

  Kfrzeilenwert,
  Listeneintrag,
  Periodenbericht,
  Periodenzeile,
} from './typen'

import { Kfrloeschfenster } from './Kfrloeschfenster'
import { Referenzanlage, Referenzloeschung, Statistikkontoanlage, referenzname } from './Kfrreferenzen'
import { rufeLoeschen } from './Elementloeschung'
import { Reihenfolgefenster } from './Reihenfolgefenster'
import type { Reihenfolgeziel } from './Reihenfolgefenster'
import { abschnitt, abschnittstitel, useKfrabschnitte } from './Kfrabschnitte'
import { nichtUmgerechnetIn } from './Anzeigewaehrung'

/**
 * **Rechtsklick → Unterzeile oder Zeile danach anlegen** (2.8 Abschnitt 13.36,
 * R1124) — an jeder Zeile, mit dem, was in die neue Zeile läuft.
 */
/**
 * **Angelegt wird nur darunter** (2.1 Abschnitt 3.6, R1706): `unter` einer
 * Position, oder unter dem Band eines Abschnitts — dann nennt `zeile` eine
 * Position dieses Abschnitts. Unter dem Band entsteht auch eine Summenzeile.
 */
export type Kfranlageruf = (zeile: string, name: string, art: 'unter' | 'abschnitt', summenzeile?: boolean) => void

/**
 * **Was unter einer gefüllten Rechnung nicht steht** (2.8 Abschnitt 13.35) —
 * eine Stelle für jede Spaltenansicht:
 *
 * - **der Satz zu einem Monat ohne Vorperiode** (R1119). Je Monat ohne Lauf
 *   davor stand einer, Dezember bis Mai untereinander; die leere Spalte sagt
 *   dasselbe. Ist die ganze Rechnung leer, bleibt er ihr Grund.
 * - **der Satz zur ungetroffenen Fondsabgrenzung** (R1120). Was im Fonds
 *   steht, zeigt sein Aufriss.
 */
const NICHT_UNTER_DER_RECHNUNG: ReadonlySet<string> = new Set([
  'kfr.befund.kein-anfangsbestand',
  'kfr.befund.kontokorrent-ungewaehlt',
])

export function unterDerRechnung<B extends { schluessel: string }>(befunde: B[]): B[] {
  return befunde.filter((b) => !NICHT_UNTER_DER_RECHNUNG.has(b.schluessel))
}

/**
 * Die indirekte Kapitalflussrechnung, gegliedert nach **DRS 21** (2.8, G3) —
 * in drei Spaltenbildern.
 *
 * **Sie wird bedient wie Bilanz und GuV.** Kreis, Bewertungsebene und Periode stehen in
 * der Kontextzeile, das Spaltenbild kommt aus derselben Knopfreihe, der
 * Aufriss ist derselbe Pfad auf der Zeilenachse (R171), und die
 * Zeilenbedienung kommt aus denselben Bauteilen. Wer die Bilanz kann, kann
 * sie.
 *
 * **Die drei Bilder sind dieselben Zeilen und drei Fragen an dieselben
 * Zahlen** (R167):
 *
 * | Bild | Frage |
 * |---|---|
 * | *Konsolidierungssachverhalt* | Welcher Teil des Cashflows steht schon in der Handelsbilanz II, welcher entsteht erst durch die Konsolidierung |
 * | *Konzernstruktur* | Welche Gesellschaft bringt welchen Zahlungsstrom mit |
 * | *Perioden* | Wie hat sich der Cashflow über die Zeit entwickelt |
 *
 * Zwei Dinge bleiben in jedem Bild gleich, und beide stehen im Fachkonzept:
 * **die Spezifikation am Bericht** — eine Kapitalflussrechnung ohne die Angabe
 * ihres Schemas lässt sich nicht prüfen (R252) — und **die Kontrollzeile
 * darunter** (R1019), die sagt, ob sie aufgeht. Einen Deltaposten gibt es
 * nicht (2.8 Abschnitt 13.32, R1096).
 *
 * **Seit dem 2026-09-13 steht die Spezifikation in der Tabelle** und nicht mehr
 * darüber: in der übergreifenden Titelzeile des Kopfes, *Kapitalflussrechnung
 * (DRS 21)* (R475, R476). Der Kopf über der Rechnung ist damit fort — mit ihm
 * der Zeitraumsatz (R477) und die Fondsabgrenzung, die danach am Bereichsband
 * *4. Finanzmittelfonds* stand (R478) und seit dem 2026-10-05 gar nicht mehr
 * (R1120): Was im Fonds steht, zeigt sein Aufriss.
 */

/**
 * Welche Zelle der Kapitalflussrechnung angeklickt wurde (R213).
 *
 * **Die Zelle weiß es selbst**, und deshalb steht hier alles beisammen: die
 * Zeile mit ihrer Nummer, Gesellschaft und Konto aus dem Aufriss, die
 * Wertspalte — und im Periodenbild der Zeitraum der Spalte. Kreis, Bewertungsebene,
 * Raster und die Stellung der Stichtagskursanpassung kommen aus der
 * Kontextzeile und nicht von hier; dieselbe Trennung wie im Bericht.
 */
export type Kfrzellenzeiger = {
  zeile: string
  code: string
  zeilenname: string
  einheit: string | null
  konto: string | null
  spalte: string
  /**
   * Der Zeitraum der Spalte — nur im Periodenbild belegt.
   *
   * Ohne ihn erklärte ein Klick auf die Juni-Spalte das ganze Jahr (R269).
   */
  von: number | null
  bis: number | null
  /** Was der Aufriss über Konto und Gesellschaft hinaus festgelegt hat (7.3 Abschnitt 2f). */
  weitere?: Record<string, string | null>
}

/** Den Nachweis zu einer Zelle rufen — `undefined`, wo das Bild keinen anbietet. */
export type Kfrnachweisruf = (ziel: Kfrzellenzeiger) => void

/**
 * Der Ruf einer Zelle, und ob sie ein Konto trägt: Nur dann öffnet der
 * Doppelklick den Nachweis, sonst klappt er die Zeile auf (7.3 Abschnitt 27,
 * R1032). Aus dem Menü wirkt der Ruf auf jeder Zeile.
 */
export type Zellruf = (() => void) & { kontowert: boolean }

/**
 * Baut den Ruf für **eine** Zelle — an einer Stelle für alle drei Bilder.
 *
 * **Eine Summenzeile bekommt keinen** (R270): Sie hat keine eigenen Beiträge,
 * ihr Wert steht in ihren Summanden, und der Nachweis dazu wären tausend
 * Belegzeilen. Der Weg dorthin führt über die Zeilen darüber — deshalb gibt
 * die Zelle hier `undefined` zurück und sieht dann auch nicht anklickbar aus.
 */
export function nachweisruf<B extends Zelle>(
  ruf: Kfrnachweisruf | undefined,
  zeile: Kfrzeilenkopf<B>,
  einheit: string | null,
  konto: string | null,
  spalte: string,
  zeitraum?: { von: number; bis: number },
  weitere?: Record<string, string | null>,
): Zellruf | undefined {
  if (!ruf || zeile.istSumme) {
    return undefined
  }

  return Object.assign(
    () =>
      ruf({
        zeile: zeile.zeile,
        code: zeile.code,
        zeilenname: beschriftung(zeile.namen),
        einheit,
        konto,
        spalte,
        von: zeitraum?.von ?? null,
        bis: zeitraum?.bis ?? null,
        weitere,
      }),
    { kontowert: konto !== null },
  )
}

/**
 * Der Nachweis aus dem Kontextmenü — dieselbe Auskunft wie der Doppelklick,
 * mit der Spalte und dem Zeitraum der Zelle, auf der das Menü aufging.
 */
export function herkunftAusMenue<B extends Zelle>(
  ruf: Kfrnachweisruf | undefined,
  zeile: Kfrzeilenkopf<B>,
  pfad: Zelle[],
  kontext: Zellkontext,
): (() => void) | undefined {
  if (kontext.spalte === '') {
    return undefined
  }

  const letzte = pfad[pfad.length - 1] ?? { einheit: null, konto: null }
  return nachweisruf(
    ruf,
    zeile,
    letzte.einheit,
    letzte.konto,
    kontext.spalte ?? 'konzern',
    kontext.von !== undefined && kontext.bis !== undefined ? { von: kontext.von, bis: kontext.bis } : undefined,
    weitereVon(pfad),
  )
}

/** Was jede Zeile trägt, gleich in welchem Bild sie steht. */
export type Kfrzeilenkopf<B extends Zelle> = {
  zeile: string
  code: string
  namen: Record<string, string>
  bereich: string
  istSumme: boolean
  zahlungswirksam: boolean
  /** Die Ausgangszeile der indirekten Darstellung (R489) — sie klappt in die GuV-Gliederung auf. */
  istPeriodenergebnis: boolean
  beitraege: B[]
  /** Wie viele Untersummen über der Zeile stehen — so tief wird sie eingerückt (R1126). */
  ebene?: number
  /** Eine Summe, die wie eine Zeile dasteht — 22 über 22a und 22b (R1126). */
  istUntersumme?: boolean
  /** Das Format einer Summenzeile (R1359). */
  format?: Kfrformat | null
  /** Ob die Zeile einen Bestand zeigt — der Fonds am Anfang oder am Ende (R1018); dort stehen seine Referenzen (R1712). */
  bestand?: 'anfang' | 'ende' | null
}

/**
 * **Eine Referenz als eigene Zeile, mit den Zahlen ihres Bildes** (2.1
 * Abschnitt 3.6, R1710). Jedes Bild liefert die Zellen, weil nur es weiß,
 * welche Spalten es hat.
 */
export type Referenzzeile = Kfrreferenzkopf & { werte: React.ReactNode }

/**
 * **Die Klassen, die das Format einer Summenzeile trägt** (2.8 Abschnitt
 * 13.46, R1359) — an einer Stelle für jedes Bild: Perioden, Konzernstruktur,
 * Partner. Was fehlt, bleibt die Form ihrer Art.
 */
export function formatklassen(format: Kfrformat | null | undefined): string[] {
  if (!format) {
    return []
  }
  return [
    format.schrift ? `format-${format.schrift}` : '',
    format.hintergrund ? `grund-${format.hintergrund}` : '',
    format.schriftfarbe ? `farbe-${format.schriftfarbe}` : '',
  ].filter(Boolean)
}

/**
 * Das Gerüst aller drei Bilder: Nummernspalte, Bezeichnung, Bereichsband,
 * Vermerke an der Zeile, Aufriss.
 *
 * **Was sich zwischen den Bildern unterscheidet, sind die Wertspalten** — und
 * die kommen als Funktion herein, genau wie bei `Aufrisszeilen`. Dieselbe
 * Bauweise wie im Bericht: Der Aufriss betrifft die Zeilenachse, und die
 * Spalten wissen nichts davon.
 */
export function Kfrtabelle<B extends Zelle, Z extends Kfrzeilenkopf<B>>({
  zeilen,
  kopfzellen,
  spaltenzahl,
  werte,
  aufrisswerte,
  aufriss,
  quelle,
  treffer,
  herkunft,
  kennung,
  waehrung,
  gliederung,
  aufAnlegen,
  aufAenderung,
  kontrolle,
  standard,
  referenzen,
}: {
  zeilen: Z[]
  /**
   * **Die Referenzen einer Position als eigene Zeilen** (2.1 Abschnitt 3.6,
   * R1710, R1712) — eine Stufe unter ihr, mit den Zahlen des Bildes.
   */
  referenzen?: (zeile: Z) => Referenzzeile[]
  /** Der Zeilensatz — für Nummer und Namen seiner Abschnitte (2.8 Abschnitt 13.41, R1333). */
  standard?: string | null
  kopfzellen: React.ReactNode
  spaltenzahl: number
  /**
   * Die Währung der Rechnung — nur, wenn sie in ihrer Ausgangswährung bleibt,
   * obwohl eine andere gewählt ist (2.8 Abschnitt 13.35, R1118). Sonst nichts.
   */
  waehrung?: string | null
  /** Welches Bild seine Spaltenbreiten merkt (7.3, Abschnitt 10). */
  kennung: string
  werte: (zeile: Z, id: string) => React.ReactNode
  aufrisswerte: (zelle: B, kennung: string, zeile: Z, pfad: Zelle[]) => React.ReactNode
  aufriss: Aufrissbedienung
  /** Woher die Zusatzachsen kommen (7.3 Abschnitt 2f). */
  quelle?: Achsenquelle<B>
  treffer?: Trefferzustand
  /** Der Nachweis aus dem Menü — `undefined`, wo es keinen gibt. */
  herkunft?: (zeile: Z, pfad: Zelle[], kontext: Zellkontext) => (() => void) | undefined
  /**
   * Die GuV-Gliederung unter dem Periodenergebnis (**R489**) — `undefined`,
   * wo das Bild sie nicht anbieten kann.
   *
   * **Ein Bauteil, das die Zeile liefert, und nicht eine Achse.** Die
   * GuV-Gliederung ist kein Aufriss: Sie hat Zwischensummen und Ebenen, und
   * `Aufrisszeilen` kennt nur flache Listen je Ebene. Das Bild weiß, welche
   * Spalten es hat und woher die Zahlen dazu kommen — deshalb baut es die
   * Zeilen, und diese Tabelle entscheidet nur, *wann* sie dastehen.
   */
  gliederung?: (zeile: Z) => React.ReactNode
  /**
   * Rechtsklick → Anlegen: die **fünfte Gruppe** (R494, R507).
   *
   * **Hier heißt Kind-Element etwas anderes:** Eine Zeile der
   * Kapitalflussrechnung steht im Zeilenkatalog des Standards und nicht im
   * Positionsplan. Angeboten werden *Unterzeile anlegen* und *Zeile danach
   * anlegen* — seit dem 2026-10-05 an **jeder** Zeile (R1124); bis dahin nur
   * an einer Summe. Ein Konto hängt an keiner KFR-Zeile.
   */
  aufAnlegen?: Kfranlageruf
  /**
   * Nach dem Schließen des Eigenschaften-Fensters, wenn darin geschrieben
   * wurde (R857): Eine geänderte Regel ändert die Zahlen, und die Rechnung
   * soll sie zeigen, ohne dass jemand neu lädt.
   */
  aufAenderung?: () => void
  /**
   * **Die Kontrollzeile** (2.8 Abschnitt 13.26, R1019): je Wertspalte eine
   * `Probezelle` — das grüne Häkchen, wo Zeile 40 den Bestand der Fondskonten
   * trifft, sonst die Differenz. Jedes Bild liefert seine Zellen, weil nur es
   * weiß, welche Spalten es hat.
   */
  kontrolle?: React.ReactNode
}) {
  const { menue, setMenue } = aufriss
  const { breiten, rahmen } = useSpaltenbreiten<HTMLTableElement>(kennung, 1)
  /** Die Zeile, deren Eigenschaften gerade im Fenster stehen (R857). */
  const [eigenschaften, setEigenschaften] = useState<Eigenschaftsziel | null>(null)

  /** Die Ebene, deren Reihenfolge gerade im Fenster steht (7.4 Abschnitt 32, R1341). */
  const [reihenfolge, setReihenfolge] = useState<Reihenfolgeziel | null>(null)
  /** Die Position oder Summenzeile, deren Löschen gerade gefragt wird (R1707). */
  const [loeschen, setLoeschen] = useState<{ zeile: string; titel: string; summenzeile: boolean } | null>(null)
  /**
   * Das Menü am Band eines Abschnitts (R1333) — **das Band ist eine
   * Position** (2.1 Abschnitt 3.6, R1711): Unter ihm entstehen Positionen und
   * Summenzeilen; `erste` ist die erste Position des Abschnitts.
   */
  // Was der Angemeldete nicht darf, ist nicht da (R1180).
  const darfLoeschen = useDarf()('aussondern')
  /** Das Menü an einer Referenz, und was es geöffnet hat (2.1 Abschnitt 3.6, R1705). */
  const [referenzmenue, setReferenzmenue] = useState<{ x: number; y: number; referenz: Referenzzeile; position: string } | null>(null)
  const [referenzanlage, setReferenzanlage] = useState<{ zeile: string; titel: string; fonds: boolean } | null>(null)
  /** Die Position, unter der gerade ein Statistikkonto entsteht (2.1 Abschnitt 3.6, R1727). */
  const [statistikanlage, setStatistikanlage] = useState<{ zeile: string; titel: string } | null>(null)
  const [referenzloeschen, setReferenzloeschen] = useState<{ referenz: Referenzzeile; position: string } | null>(null)
  const [bandmenue, setBandmenue] = useState<{
    x: number
    y: number
    schluessel: string
    titel: string
    erste: string
    bereich: string
  } | null>(null)
  /** Steigt, wenn ein Abschnitt gepflegt wurde — dann werden die Namen neu geholt. */
  const [abschnittsstand, setAbschnittsstand] = useState(0)
  const abschnitte = useKfrabschnitte(abschnittsstand)

  const aufMenue: Menueruf = (x, y, position, eltern, zelle, kontext) =>
    setMenue({ x, y, position, eltern, zelle, kontext })

  // **Eine Untersumme klappt wie ein Summenposten der GuV** (2.8 Abschnitt
  // 13.37, R1321) — derselbe Baustein wie in Bilanz, GuV und den Spiegeln.
  // Die Rechnung liefert sie über ihren Unterzeilen (R1320); die Ebene sagt
  // den Rest. Der Cashflow eines Abschnitts steht auf Ebene 0 und klappt nicht.
  //
  // **Die Referenzen stehen eine Stufe unter ihrer Position** (2.1 Abschnitt
  // 3.6, R1710) und gehören zur Gliederung wie in der Bilanz die Konten: Die
  // Ebenenwahl und der Pfeil der Position klappen sie zu.
  const klappe = useGliederungsbild(
    zeilen.flatMap((zeile) => [
      { position: zeile.zeile, ebene: zeile.ebene ?? 0, zeile, referenz: undefined as Referenzzeile | undefined },
      ...(referenzen?.(zeile) ?? []).map((referenz) => ({
        position: `${zeile.zeile}→${referenz.schluessel}`,
        ebene: (zeile.ebene ?? 0) + 1,
        zeile,
        referenz: referenz as Referenzzeile | undefined,
      })),
    ]),
  )
  const sichtbar = klappe.zeilen.filter((g) => g.referenz === undefined).map((g) => g.zeile)
  const referenzzeilen = (zeile: string) =>
    klappe.zeilen.filter((g) => g.referenz !== undefined && g.zeile.zeile === zeile)

  // Die vier Abschnitte des DRS 21. Die Überschrift steht in der Tabelle und
  // nicht darüber: Der Bereich ist Teil der Gliederung, nicht ihre Umgebung —
  // wer die Rechnung ausdruckt, braucht ihn zwischen den Zeilen.
  const bereichsgrenzen = new Map<string, string>()
  for (const b of ['operativ', 'investition', 'finanzierung', 'fonds']) {
    const erste = zeilen.find((z) => z.bereich === b)
    if (erste) {
      bereichsgrenzen.set(erste.zeile, b)
    }
  }

  return (
    <>
      <table className="bericht kapitalfluss positionstabelle" ref={rahmen}>
        {/*
          Die Nummernspalte des DRS 21 bleibt vier Zeichen breit -- plus das
          Polster der Zelle, denn in einer fest aufgeteilten Tabelle zaehlt der
          Rahmen mit.
        */}
        <Spaltenleiste breiten={breiten} spalten={spaltenzahl} vorspalten={['calc(4ch + 20px)']} />
        <thead>
          {/*
            **Der Name des Berichts steht in der Tabelle** (R475). Anders als
            Bilanz, GuV und die Spiegel hat die Kapitalflussrechnung kein
            freies Feld im Kopf: Er ist einstöckig, und die Nummernspalte ist
            vier Zeichen breit. Also eine übergreifende Zeile über allen
            Spalten — im Wortlaut des Arbeitsbereichs, mitsamt der
            Spezifikation (R252, R476): Ein ausgedrucktes Blatt nennt sein
            Schema selbst.

            **Im Kopf und nicht als erste Zeile des Körpers:** Der Kopf klebt
            als Ganzer (R412), und damit steht der Name auch bei Zeile achtzig
            noch da.
          */}
          <tr className="kfrtitelzeile">
            <th colSpan={spaltenzahl} className="berichtstitel">
              {/*
                **Die Währung steht nur, wenn sie überrascht** (R1118): Ein
                Umrechnungssatz über der Rechnung steht nicht mehr. Fehlt ein
                Kurs dort, wo er einen Betrag trifft, sagt es die Titelzeile —
                sonst stünden USD-Zahlen wortlos unter der Wahl EUR.
              */}
              {waehrung
                ? t('kfr.titel.inwaehrung', { titel: t('bereich.berichte.kapitalflussrechnung'), waehrung })
                : t('bereich.berichte.kapitalflussrechnung')}
            </th>
          </tr>
          <tr>
            <th className="kfrnummer">{t('kfr.spalte.nummer')}</th>
            <th className="positionsspalte">{t('kfr.spalte.zeile')}</th>
            {kopfzellen}
          </tr>
        </thead>
        <tbody>
          {sichtbar.map((zeile) => {
            const gemerkt = aufriss.zustand.get(ebenenschluessel(zeile.zeile, []))

            /*
              **Das Periodenergebnis klappt in die GuV-Gliederung auf** (R489),
              und zwar als Vorgabe. Wer diese Zeile aufklappt, sucht den
              Sichtabgleich — die Konten darunter beantworten eine andere
              Frage und stehen weiter im Menü.

              Erkannt wird sie am **Merkmal**, nicht am Schlüssel (R157): Im
              DRS 21 heißt sie KFR-01, im IAS 7 KFR7-01, und im nächsten
              Zeilensatz anders.
            */
            const mitGliederung = gliederung !== undefined && zeile.istPeriodenergebnis

            // **Ein Bild, das die Gliederung nicht anbietet, zeigt die Zeile
            // zu.** Der Aufklappzustand gilt bildübergreifend (R171); wer im
            // Konsolidierungsbild aufklappt und ins Partnerbild wechselt,
            // sähe sonst einen offenen Pfeil über nichts.
            const achse = gemerkt === ACHSE_GUV && !mitGliederung ? undefined : gemerkt

            const vorgabe = mitGliederung ? ACHSE_GUV : vorgabeFuer(zeile.beitraege)
            const moeglich = mitGliederung
              ? [ACHSE_GUV, ...moeglicheAchsen(zeile.beitraege, [], zusatzachsen(quelle))]
              : moeglicheAchsen(zeile.beitraege, [], zusatzachsen(quelle))
            // Eine Summenzeile hat keine eigenen Beiträge und trägt kein Menü:
            // Ihr Wert steht in ihren Summanden (R270). Die Bedingung stand hier
            // nachgebaut; sie gehört an eine Stelle für alle fünf Tabellen (R432).
            const aufreissbar = istAufreissbar(zeile, moeglich)
            const leereZelle: Zelle = { einheit: null, konto: null }
            const id = zeilenId(zeile.zeile, [])
            const bereich = bereichsgrenzen.get(zeile.zeile)
            const band = bereich ? abschnitt(abschnitte, standard, bereich) : null

            return (
              <Fragmentzeile key={zeile.zeile}>
                {bereich && (
                  <tr
                    className="kfrbereich"
                    // **Rechtsklick auf das Band → Eigenschaften** (2.8 Abschnitt
                    // 13.41, R1333): Nummer und Name des Abschnitts.
                    onContextMenu={(e) => {
                      if (!band) return
                      e.preventDefault()
                      setBandmenue({
                        x: e.clientX,
                        y: e.clientY,
                        schluessel: band.schluessel,
                        titel: band.titel,
                        erste: zeile.zeile,
                        bereich,
                      })
                    }}
                  >
                    <th colSpan={spaltenzahl}>
                      {/*
                        **Kein Klammervermerk zur Fondsabgrenzung** (2.8
                        Abschnitt 13.35, R1120): Was im Fonds steht, zeigt sein
                        Aufriss, und ändern lässt sich die Wahl mit den
                        Lieferdaten nicht. Bis zum 2026-10-05 stand hier
                        *(Fondsabgrenzung nicht gewählt)* (R478).
                      */}
                      {abschnittstitel(abschnitte, standard, bereich)}
                    </th>
                  </tr>
                )}

                <tr
                  id={id}
                  className={[
                    // **Eine Untersumme steht wie eine Zeile da** (R1126) —
                    // 22 über 22a und 22b ist kein Cashflow eines Abschnitts.
                    zeile.istSumme && !zeile.istUntersumme ? 'summe' : '',
                    // Das Format, das der Anwender der Summenzeile gab (R1359).
                    ...formatklassen(zeile.format),
                    aufreissbar ? 'aufklappbar' : '',
                    // Die Pflichtgliederung steht vollständig da, und eine leere
                    // Zeile ist eine Aussage — „erhaltene Dividenden: keine".
                    // Getragen wird sie vom leeren Wertfeld; gedämpft wird sie
                    // seit dem 2026-10-02 nicht mehr (R867, 7.3 Abschnitt 21).
                    trefferklasse(treffer, id),
                  ]
                    .join(' ')
                    .trim()}
                  /*
                    **An einer Summenzeile gab es bis zum 2026-09-13 kein
                    Menü** — sie reißt nichts auf, und ein Menü ohne Punkt ist
                    ein Klick, der nichts gibt (R171a). Seit dem Anlegen (R507)
                    gibt es dort einen Punkt, und zwar genau dort: Eine neue
                    Zeile entsteht unter ihrer Summe. Ohne Anlegeruf bleibt es,
                    wie es war.
                  */
                  tabIndex={!zeile.istSumme || aufAnlegen ? 0 : undefined}
                  onContextMenu={(e) => {
                    if (zeile.istSumme && !aufAnlegen) {
                      return
                    }

                    e.preventDefault()
                    const ort = menueort(e, e.currentTarget)
                    aufMenue(ort.x, ort.y, zeile.zeile, [], leereZelle, zellkontext(e))
                  }}
                >
                  <td className="kfrnummer">{zeile.code}</td>
                  {/* Derselbe schmale Rand wie in jedem anderen Bericht. Eine
                      Ebene hat die Kapitalflussrechnung nur unter einer
                      Untersumme: 22a und 22b stehen eine Stufe tiefer (R1126). */}
                  <td style={{ paddingLeft: einzug(zeile.ebene ?? 0) }}>
                    {zeile.istSumme && !zeile.istUntersumme ? (
                      beschriftung(zeile.namen)
                    ) : (
                      <Zeilenkopf
                        aufreissbar={aufreissbar}
                        achse={achse}
                        vorgabe={vorgabe}
                        angebot={quelle}
                        aufKlappen={() =>
                          aufriss.umschalten(zeile.zeile, [], achse ?? moeglich[0] ?? vorgabe)
                        }
                        aufMenue={(x, y) => aufMenue(x, y, zeile.zeile, [], leereZelle, {})}
                        klappbar={klappe.klappbar(zeile.zeile)}
                        zugeklappt={klappe.zugeklappt(zeile.zeile)}
                        aufGliederung={() => klappe.umschalten(zeile.zeile)}
                      >
                        {beschriftung(zeile.namen)}
                      </Zeilenkopf>
                    )}

                    {/* Hier stand bis zum 2026-10-04 „nicht zahlungswirksam"
                        an jeder Zeile, die keinen Zahlungsstrom trägt. Entfernt
                        auf Auftrag (2.8 Abschnitt 13.24): Dem Anwender ist das
                        aus dem engen fachlichen Rahmen der Rechnung bekannt. */}

                    {/*
                      **Kein Deltaposten an der Zeile** (2.8 Abschnitt 13.32,
                      R1096). Bis zum 2026-10-05 stand hier „davon Deltaposten"
                      und an 13a „ungeklärt — bitte ansehen". Ein Betrag, der
                      sich nicht herleiten läßt, steht in keiner Zeile; die
                      Kontrollzeile und die Abstimmung zeigen ihn (R1097).

                      **Die Vermerke stehen am rechten Rand der Namenszelle**
                      (Befund vom 2026-10-05): Die Spalte ist fest breit und
                      kürzt den Namen. Hinter „Auszahlungen für Investitionen
                      in das Sachanlagevermögen" war die Marke abgeschnitten.
                    */}
                    {/*
                      **Die Marke „falsches Vorzeichen" ist fort** (2.1
                      Abschnitt 3.6, R1713): Was eine Referenz bringen muss,
                      kann CoKo nicht wissen; die Abstimmung nennt, was fehlt
                      und was doppelt steht (R1714). Bis zum 2026-10-14 stand
                      sie hier, aus dem Namen der Zeile gelesen (R1090–R1095).
                    */}
                  </td>
                  {werte(zeile, id)}
                </tr>

                {/*
                  **Jede Referenz als eigene Zeile unter ihrer Position** (2.1
                  Abschnitt 3.6, R1710) — mit der eigenen Bezeichnung, sonst
                  der Quelle. Rechtsklick: *Referenz löschen …* für genau
                  diese (R1705).
                */}
                {referenzzeilen(zeile.zeile).map(({ referenz, ebene }) => (
                  <tr
                    key={`${zeile.zeile}→${referenz!.schluessel}`}
                    className="kfrreferenz"
                    tabIndex={0}
                    onContextMenu={(e) => {
                      e.preventDefault()
                      const ort = menueort(e, e.currentTarget)
                      setReferenzmenue({
                        x: ort.x,
                        y: ort.y,
                        referenz: referenz!,
                        position: `${zeile.code} ${beschriftung(zeile.namen)}`.trim(),
                      })
                    }}
                  >
                    <td className="kfrnummer" />
                    {/* Der Platz des Pfeils, damit die Bezeichnung unter dem Namen der Position beginnt. */}
                    <td style={{ paddingLeft: einzug(ebene) }}>
                      <Pfeilplatz />
                      {/* **Ref.** oder **Stat.** — woher der Betrag kommt, auf einen Blick (2.1 Abschnitt 3.6, R1730). */}
                      {referenz!.art === 'statistik' ? (
                        <span className="elementart" title={t('element.art.statistik')}>
                          {t('element.kurz.statistik')}
                        </span>
                      ) : (
                        <span className="elementart" title={t('element.art.referenz')}>
                          {t('element.kurz.referenz')}
                        </span>
                      )}
                      {referenzname(referenz!)}
                    </td>
                    {referenz!.werte}
                  </tr>
                ))}

                {/*
                  **Nur unter einer Zeile, die aufgerissen werden darf** (R165, R432).
                  Pfeil und Menü führen nicht auf eine Summenzeile — aber der
                  Aufklappzustand kommt auch von woanders: aus einem anderen Bericht,
                  aus der Adresse, und seit heute aus einem Suchtreffer. Ohne diesen
                  Riegel zeichnet die Tabelle, was kein Weg ihr hätte sagen dürfen.
                */}
                {aufreissbar && achse === ACHSE_GUV && gliederung?.(zeile)}

                {aufreissbar && achse !== undefined && achse !== ACHSE_GUV && (
                  <Aufrisszeilen
                    position={zeile.zeile}
                    ebeneTiefe={0}
                    zellen={zeile.beitraege}
                    eltern={[]}
                    aufriss={aufriss}
                    quelle={quelle}
                    vorgabe={vorgabe}
                    spaltenzahl={spaltenzahl}
                    vorspalte={<td className="kfrnummer" />}
                    treffer={treffer}
                    aufMenue={aufMenue}
                    werteSpalten={(z, kennung, pfad) => aufrisswerte(z, kennung, zeile, pfad)}
                  />
                )}
              </Fragmentzeile>
            )
          })}
        </tbody>
        {kontrolle && (
          <tfoot>
            <tr className="kontrollzeile">
              <td className="kfrnummer" />
              <td>{t('kfr.kontrolle')}</td>
              {kontrolle}
            </tr>
          </tfoot>
        )}
      </table>

      {menue &&
        (() => {
          // Dasselbe Menü wie im Bericht (7.3 Abschnitt 3): Nachweis, diese
          // Zeile, die ganze Rechnung — nur dass die Zeile hier eine Nummer
          // des Standards trägt und keine Position.
          const zeile = zeilen.find((z) => z.zeile === menue.position)
          const pfad = pfadVon(menue)
          const vorrat = aufriss.vorratFuer(quelle, zeile?.beitraege ?? [], menue.position, pfad)

          // Die GuV-Gliederung steht nur an der Zeile selbst zur Wahl, nicht
          // unter einem ihrer Konten: Sie zerlegt das Periodenergebnis, und
          // ein einzelnes Konto darunter hat keines (R489).
          const offeneAchsen = moeglicheAchsen(vorrat, pfad, zusatzachsen(quelle))
          const moeglich =
            gliederung !== undefined && zeile?.istPeriodenergebnis && pfad.length === 0
              ? [ACHSE_GUV, ...offeneAchsen]
              : offeneAchsen

          return (
            <Zeilenmenue
              x={menue.x}
              y={menue.y}
              punkte={menuepunkte({
                offen: aufriss.zustand.get(ebenenschluessel(menue.position, pfad)),
                moeglich,
                angebot: quelle?.angebot,
                aufAchse: (a) => aufriss.oeffnen(menue.position, pfad, a),
                aufZuklappen: () => aufriss.zuklappen(menue.position, pfad),
                aufHerkunft: zeile && herkunft ? herkunft(zeile, pfad, menue.kontext) : undefined,
                bericht: {
                  aufAlle: (a) =>
                    aufriss.alleAufklappen(
                      zeilen.map((z) => ({
                        position: z.zeile,
                        istSumme: z.istSumme,
                        beitraege: z.beitraege,
                      })),
                      a,
                    ),
                  aufZu: aufriss.alleZuklappen,
                },
                // Die Ebenenwahl wie in Bilanz und GuV (R445, R1321).
                gliederung: klappe.menue,
                // **Nach Elementtyp** (2.1 Abschnitt 3.6, R1705, R1706): An
                // einer Position *Neue Position …* darunter und *Position
                // löschen …*; an einer Summenzeile nur *Summenzeile löschen …*
                // — unter ihr hängt nichts. Bis zum 2026-10-14 hießen die
                // Punkte *Neue Unterzeile …*, *Neue Zeile …* und *Zeile
                // löschen …*. Unter einem Aufriss nichts: Ein Konto hängt an
                // keiner Position der Kapitalflussrechnung.
                anlage:
                  aufAnlegen && zeile && pfad.length === 0
                    ? (() => {
                        const titel = `${zeile.code} ${beschriftung(zeile.namen)}`.trim()
                        const summenzeile = zeile.istSumme && !zeile.istUntersumme
                        return {
                          aufPosition: summenzeile
                            ? undefined
                            : () => {
                                setMenue(null)
                                aufAnlegen(zeile.zeile, titel, 'unter')
                              },
                          // **Neue Referenz …** an einer Position, und am Fonds
                          // am Anfang und am Ende dessen Referenzen (2.1
                          // Abschnitt 3.6, R1705, R1712) — an keiner anderen
                          // Summenzeile: Sie zieht nichts.
                          aufReferenz:
                            summenzeile && !zeile.bestand
                              ? undefined
                              : () => {
                                  setMenue(null)
                                  setReferenzanlage({ zeile: zeile.zeile, titel, fonds: Boolean(zeile.bestand) })
                                },
                          // **Neues Konto …** — in der Kapitalflussrechnung ein
                          // Statistikkonto, unter genau dieser Position (2.1
                          // Abschnitt 3.6, R1715, R1727). Nicht am Fonds und
                          // an keiner Summenzeile.
                          aufKonto:
                            summenzeile || zeile.bestand
                              ? undefined
                              : () => {
                                  setMenue(null)
                                  setStatistikanlage({ zeile: zeile.zeile, titel })
                                },
                          loeschen: {
                            art: summenzeile ? ('summenzeile' as const) : ('position' as const),
                            tun: () => {
                              setMenue(null)
                              setLoeschen({ zeile: zeile.zeile, titel, summenzeile })
                            },
                          },
                        }
                      })()
                    : undefined,
                // **Reihenfolge ändern** an der Zeile selbst (7.4 Abschnitt 32,
                // R1341) — nicht unter einem Aufriss: Ein Konto hat dort keine.
                aufReihenfolge:
                  aufAnlegen && zeile && pfad.length === 0
                    ? () => {
                        setMenue(null)
                        setReihenfolge({
                          art: 'kfr_zeile',
                          element: zeile.zeile,
                          titel: `${zeile.code} ${beschriftung(zeile.namen)}`.trim(),
                        })
                      }
                    : undefined,
                // **An jeder Zeile, auch an der Summe** (R857), und unter einem
                // Aufriss das Konto oder die Gesellschaft, die dort steht (R858).
                aufEigenschaften: (() => {
                  const ziel = zeile ? zielVon('kfr_zeile', zeile.zeile, pfad) : null
                  return ziel
                    ? () => {
                        setMenue(null)
                        setEigenschaften(
                          pfad.length === 0
                            ? { ...ziel, titel: `${zeile!.code} ${beschriftung(zeile!.namen)}`.trim() }
                            : ziel,
                        )
                      }
                    : undefined
                })(),
              })}
              aufSchliessen={() => setMenue(null)}
            />
          )
        })()}

      {eigenschaften && (
        <Eigenschaftsfenster
          ziel={eigenschaften}
          aufSchliessen={(geaendert) => {
            setEigenschaften(null)
            if (geaendert) {
              setAbschnittsstand((s) => s + 1)
              aufAenderung?.()
            }
          }}
        />
      )}

      {reihenfolge && (
        <Reihenfolgefenster
          ziel={reihenfolge}
          aufSchliessen={(geaendert) => {
            setReihenfolge(null)
            if (geaendert) {
              aufAenderung?.()
            }
          }}
        />
      )}

      {loeschen && (
        <Kfrloeschfenster
          zeile={loeschen.zeile}
          titel={loeschen.titel}
          summenzeile={loeschen.summenzeile}
          aufGeloescht={() => aufAenderung?.()}
          aufSchliessen={() => setLoeschen(null)}
        />
      )}

      {referenzmenue && (
        <Zeilenmenue
          x={referenzmenue.x}
          y={referenzmenue.y}
          punkte={menuepunkte({
            offen: undefined,
            moeglich: [],
            aufAchse: () => {},
            aufZuklappen: () => {},
            anlage: aufAnlegen && darfLoeschen
              ? {
                  // **Konto löschen …** an einem Statistikkonto (2.1 Abschnitt
                  // 3.6, R1705, R1707): wie in Bilanz und GuV — ein bebuchtes
                  // wird stillgelegt, ein nie bebuchtes verschwindet samt
                  // seiner Zuordnung.
                  loeschen:
                    referenzmenue.referenz.art === 'statistik' && referenzmenue.referenz.konto
                      ? {
                          art: 'konto',
                          tun: () => {
                            rufeLoeschen({
                              art: 'konto',
                              schluessel: referenzmenue.referenz.konto!,
                              name: referenzname(referenzmenue.referenz),
                              mitnehmen: { objektart: 'kfr_zuordnung', schluessel: referenzmenue.referenz.schluessel },
                            })
                            setReferenzmenue(null)
                          },
                        }
                      : {
                          art: 'referenz',
                          tun: () => {
                            setReferenzloeschen({ referenz: referenzmenue.referenz, position: referenzmenue.position })
                            setReferenzmenue(null)
                          },
                        },
                }
              : undefined,
            // **Eigenschaften** — ihr Stammdatenblatt mit der Bezeichnung (R1710);
            // am Statistikkonto das des Kontos.
            aufEigenschaften: () => {
              const statistik = referenzmenue.referenz.art === 'statistik' && referenzmenue.referenz.konto
              setEigenschaften({
                objektart: statistik ? 'konto' : referenzmenue.referenz.art === 'fonds' ? 'kfr_fonds' : 'kfr_zuordnung',
                schluessel: statistik ? referenzmenue.referenz.konto! : referenzmenue.referenz.schluessel,
                titel: referenzname(referenzmenue.referenz),
              })
              setReferenzmenue(null)
            },
          })}
          aufSchliessen={() => setReferenzmenue(null)}
        />
      )}

      {referenzanlage && (
        <Referenzanlage
          zeile={referenzanlage.zeile}
          titel={referenzanlage.titel}
          fonds={referenzanlage.fonds}
          aufAngelegt={() => aufAenderung?.()}
          aufSchliessen={() => setReferenzanlage(null)}
        />
      )}

      {statistikanlage && (
        <Statistikkontoanlage
          zeile={statistikanlage.zeile}
          titel={statistikanlage.titel}
          aufAngelegt={() => aufAenderung?.()}
          aufSchliessen={() => setStatistikanlage(null)}
        />
      )}

      {referenzloeschen && (
        <Referenzloeschung
          referenz={referenzloeschen.referenz}
          position={referenzloeschen.position}
          aufGeloescht={() => aufAenderung?.()}
          aufSchliessen={() => setReferenzloeschen(null)}
        />
      )}

      {bandmenue && (
        <Zeilenmenue
          x={bandmenue.x}
          y={bandmenue.y}
          punkte={[
            /*
              **Unter dem Band: Neue Position … und Neue Summenzeile …** (2.1
              Abschnitt 3.6, R1706, R1711) — am Ende des Abschnitts, nicht im
              Finanzmittelfonds: Den Fonds am Ende zählte dort nichts mit.
            */
            ...(aufAnlegen && bandmenue.bereich !== 'fonds'
              ? ([
                  {
                    schluessel: 'anlage.menue.position',
                    tun: () => {
                      setBandmenue(null)
                      aufAnlegen(bandmenue.erste, bandmenue.titel, 'abschnitt')
                    },
                  },
                  {
                    schluessel: 'anlage.menue.summenzeile',
                    tun: () => {
                      setBandmenue(null)
                      aufAnlegen(bandmenue.erste, bandmenue.titel, 'abschnitt', true)
                    },
                  },
                  { art: 'trenner' },
                ] as const)
              : []),
            {
              schluessel: 'menue.eigenschaften',
              tun: () => {
                setEigenschaften({ objektart: 'kfr_abschnitt', schluessel: bandmenue.schluessel, titel: bandmenue.titel })
                setBandmenue(null)
              },
            },
          ]}
          aufSchliessen={() => setBandmenue(null)}
        />
      )}


    </>
  )
}

/**
 * Das Bild **Konsolidierungssachverhalt** — die sieben Spalten des Berichts.
 *
 * Ein Fachanwender will nicht nur den Cashflow sehen, sondern den Weg dorthin:
 * Welcher Teil steht schon in der Handelsbilanz II, und welcher entsteht erst
 * durch die Konsolidierung. Im Demokonzern ist genau das die Antwort auf
 * „warum ist die Abschreibungszeile größer als die Summe der Einzelbilanzen" —
 * die Firmenwertabschreibung und die Abschreibung der stillen Reserven stehen
 * in der Spalte *Kapital*.
 */
export function Kapitalflussrechnung({
  rechnung,
  plan,
  aufriss,
  quelle,
  treffer,
  aufAenderung,
  aufNachweis,
  aufAnlegen,
}: {
  rechnung: Kfrsatz
  /** Die Gliederung, in der die GuV darunter gelesen wird (R489) — dieselbe wie im Bericht. */
  plan?: string | null
  aufriss: Aufrissbedienung
  quelle?: Achsenquelle<Kfrbeitrag>
  treffer?: Trefferzustand
  /** Nach einer erfassten Umgliederung: die Rechnung neu holen. */
  aufAenderung?: () => void
  aufNachweis?: Kfrnachweisruf
  /** Rechtsklick → Unterzeile oder Zeile danach (R494, R1124). */
  aufAnlegen?: Kfranlageruf
}) {
  // **Vor dem frühen Ausstieg**, weil ein Haken keine Bedingung verträgt: Eine
  // leere Rechnung hat keine Gliederung zu zeigen, und der Abruf läuft dann
  // auch nicht — `anfordern` wird nie gerufen.
  const guv = useErgebnisgliederung(rechnung.grundlage, plan)

  if (rechnung.zeilen.length === 0) {
    return <Leer rechnung={rechnung} />
  }

  /*
    **Die Endspalte trägt den Namen ihrer Bewertungsebene** (R777).

    Die Spaltenfolge ist dieselbe wie im Bericht: Handelsbilanz II, die
    Konsolidierungsspalten, dann ihre Summe. Konsolidierungssachverhalte sind
    ein Typ derselben Dimension (Auftraggeber, 2026-09-26) — die Endspalte ist
    damit die Summe der Gruppe.

    **Bis zum 2026-09-26 stand dort ein fester Text** — *Konzern*. Er sagte
    nichts über die Bewertungsebene, für die gerechnet wurde, und ein
    umbenanntes Stammdatum kam nie an.
  */
  const spalten: { kennung: string; schluessel?: Sprachschluessel; kopf?: string; klasse?: string }[] = [
    { kennung: 'hb2', schluessel: 'spalte.hb2' },
    { kennung: 'kapko', schluessel: 'spalte.kapko' },
    { kennung: 'schuko', schluessel: 'spalte.schuko' },
    { kennung: 'aeko', schluessel: 'spalte.aeko' },
    { kennung: 'zwerge_uv', schluessel: 'spalte.zwerge_uv' },
    { kennung: 'zwerge_av', schluessel: 'spalte.zwerge_av' },
    {
      kennung: 'konzern',
      kopf: rechnung.gruppe
        ? kurzform(rechnung.gruppe.kuerzel, rechnung.gruppe.namen) || rechnung.gruppe.code
        : t('spalte.konzern'),
      klasse: 'konzern',
    },
  ]

  // Die Kennung ist die der Datenbankspalte — sie steht so in `core.laufzeile`
  // und im Wertenachweis, und ein zweiter Name für dieselbe Spalte ginge mit
  // der Zeit auseinander. Im Übergabesatz heißen sie nur anders geschrieben,
  // weil C# und TypeScript dort zusammenkommen: aus `zwerge_uv` wird
  // `zwErgEUv`. Die Umschrift steht deshalb hier und nirgends sonst.
  const FELD: Record<string, string> = {
    kapko: 'kapKo',
    schuko: 'schuKo',
    aeko: 'aeKo',
    zwerge_uv: 'zwErgEUv',
    zwerge_av: 'zwErgEAv',
  }

  const wert = (z: Kfrzeilenwert | Kfrbeitrag, kennung: string): number =>
    kennung === 'konzern'
      ? z.betrag
      : ((z as unknown as Record<string, number>)[FELD[kennung] ?? kennung] ?? 0)

  // Dieselben sieben Spalten, andere Herkunft der Konzernzahl: Eine
  // Berichtszeile trägt sie als `konzern`, eine Zeile der
  // Kapitalflussrechnung als `betrag`. Die sechs
  // Konsolidierungsspalten heißen in beiden gleich (R489).
  const guvwert = (z: Berichtszeile, kennung: string): number =>
    kennung === 'konzern'
      ? z.konzern
      : ((z as unknown as Record<string, number>)[FELD[kennung] ?? kennung] ?? 0)

  return (
    <div className="tabellenrahmen">
      <Kfrtabelle<Kfrbeitrag, Kfrzeilenwert>
        standard={rechnung.standard}
        aufAnlegen={aufAnlegen}
        aufAenderung={aufAenderung}
        kennung="kfr:konsolidierung"
        // **Jede Referenz mit ihren sieben Spalten** (2.1 Abschnitt 3.6, R1710).
        referenzen={(zeile) =>
          (zeile.referenzen ?? []).map((r) => ({
            ...r,
            werte: spalten.map((s) => (
              <Zahl key={s.kennung} wert={wert(r as unknown as Kfrbeitrag, s.kennung)} klasse={s.klasse} spalte={s.kennung} />
            )),
          }))
        }
        waehrung={nichtUmgerechnetIn(rechnung.umrechnung)}
        zeilen={rechnung.zeilen}
        spaltenzahl={2 + spalten.length}
        kopfzellen={spalten.map((s) => (
          <th key={s.kennung} className={`zahl ${s.klasse ?? ''}`.trim()}>
            {s.kopf ?? t(s.schluessel!)}
          </th>
        ))}
        werte={(zeile, id) =>
          spalten.map((s) => (
            <Zahl
              key={s.kennung}
              wert={wert(zeile, s.kennung)}
              klasse={s.klasse}
              fund={istTrefferzelle(treffer, id, s.kennung)}
              spalte={s.kennung}
              aufNachweis={nachweisruf(aufNachweis, zeile, null, null, s.kennung)}
            />
          ))
        }
        aufrisswerte={(z, kennung, zeile) =>
          spalten.map((s) => (
            <Zahl
              key={s.kennung}
              wert={wert(z, s.kennung)}
              klasse={s.klasse}
              fund={istTrefferzelle(treffer, kennung, s.kennung)}
              spalte={s.kennung}
              aufNachweis={nachweisruf(aufNachweis, zeile, z.einheit, z.konto, s.kennung, undefined, z.weitere)}
            />
          ))
        }
        aufriss={aufriss}
        quelle={quelle}
        treffer={treffer}
        herkunft={(zeile, pfad, kontext) => herkunftAusMenue(aufNachweis, zeile, pfad, kontext)}
        kontrolle={spalten.map((s) => (
          <Probezelle
            key={s.kennung}
            wert={
              s.kennung === 'konzern'
                ? rechnung.abweichung
                : rechnung.fondsprobe
                  ? wert(rechnung.fondsprobe as unknown as Kfrzeilenwert, s.kennung)
                  : undefined
            }
            klasse={s.klasse}
          />
        ))}
        gliederung={() => (
          <Ergebnisgliederung<Berichtszeile>
            zeilen={guv.zeilen}
            anfordern={guv.anfordern}
            spaltenzahl={2 + spalten.length}
            vorspalte={<td className="kfrnummer" />}
            werte={(z) =>
              spalten.map((s) => (
                <Zahl key={s.kennung} wert={guvwert(z, s.kennung)} klasse={s.klasse} spalte={s.kennung} />
              ))
            }
          />
        )}
      />

      <Kfrnachweis rechnung={rechnung} aufAenderung={aufAenderung} />
    </div>
  )
}

/**
 * Das Bild **Perioden** — je Zeitraum eine Spalte.
 *
 * **Jede Spalte ist eine vollständige eigene Rechnung**, kein Ausschnitt aus
 * einer großen: eigener Zeitraum, eigener Anfangsbestand, eigener
 * Fondsnachweis. Der Finanzmittelfonds am Anfang eines Quartals ist nicht ein
 * Viertel des Jahresanfangsbestands.
 *
 * Man sieht das an den Zeilen 39 und 40: Der Endbestand einer Spalte ist der
 * Anfangsbestand der nächsten. **Das ist zugleich die Probe** — eine Kette,
 * die nicht schließt, wäre auf einen Blick sichtbar.
 */
export function KapitalflussNachPerioden({
  bericht,
  aufriss,
  quelle,
  treffer,
  aufNachweis,
  aufAnlegen,
  aufAenderung,
  kumuliert = false,
  guvLaden,
  guvKennung = '',
}: {
  bericht: Kfrperiodenbericht
  aufriss: Aufrissbedienung
  quelle?: Achsenquelle<Kfrperiodenaufriss>
  treffer?: Trefferzustand
  aufNachweis?: Kfrnachweisruf
  /** Rechtsklick → Unterzeile oder Zeile danach (R494, R1124). */
  aufAnlegen?: Kfranlageruf
  /** Nach einer Änderung im Eigenschaften-Fenster: die Rechnung neu holen (R857). */
  aufAenderung?: () => void
  /** *Kumuliert seit Jahresbeginn*: Jede Spalte trägt schon den Stand — keine Zwischensumme (R1018). */
  kumuliert?: boolean
  /**
   * Holt die GuV mit denselben Spalten (2.8 Abschnitt 13.33, R1105) — ohne sie
   * klappt Zeile 1 nach Konten auf wie bisher.
   */
  guvLaden?: () => Promise<Periodenbericht>
  /** Worauf die geholte GuV passt: Zeitraum, Raster, Sicht. */
  guvKennung?: string
}) {
  // **Zwischensummen** (7.3 Abschnitt 26, R1018): Zahlungsströme werden
  // addiert, der Fonds am Anfang und am Ende steht als Stand.
  const optionen = useAnzeigeoptionen()

  // **Vor dem frühen Ausstieg**, wie in der Einzelrechnung: Ein Haken
  // verträgt keine Bedingung.
  const guv = useGuvJeSpalte(guvLaden, guvKennung)

  if (bericht.zeilen.length === 0 || bericht.spalten.length === 0) {
    return (
      <div className="meldung">
        <h2>{t('kfr.leer.titel')}</h2>
        {bericht.befunde.map((b, i) => (
          <p key={i} className="handreichung">
            {satz(b)}
          </p>
        ))}
        {bericht.befunde.length === 0 && <p>{t('kfr.leer.grund')}</p>}
      </div>
    )
  }

  const perioden = bericht.spalten.map((s) => s.periode)
  const zwischen = kumuliert ? [] : zwischenspalten(perioden, optionen.zwischensummen ?? new Set())
  const folge = spaltenfolge(bericht.spalten.length, zwischen)

  // **Die GuV-Spalte zur Spalte der Rechnung, über die Periode** (R1105) —
  // nicht über die Stelle: Beide Berichte schneiden denselben Zeitraum, aber
  // eine Spalte ohne Lauf darf nicht die Zahl ihrer Nachbarin bekommen.
  const guvStelle = new Map((guv.bericht?.spalten ?? []).map((s, j) => [s.periode, j]))
  const guvWerte = (z: Periodenzeile): number[] =>
    bericht.spalten.map((s) => {
      const j = guvStelle.get(s.periode)
      return j === undefined ? 0 : (z.werte[j] ?? 0)
    })

  return (
    <div className="tabellenrahmen">
      <Kfrtabelle<Kfrperiodenaufriss, Kfrperiodenzeile>
        standard={bericht.standard}
        aufAnlegen={aufAnlegen}
        aufAenderung={aufAenderung}
        kennung="kfr:perioden"
        // **Jede Referenz mit dem Betrag jeder Spalte** (2.1 Abschnitt 3.6,
        // R1710) — eine Zwischensumme wie ihre Zeile.
        referenzen={(zeile) =>
          (zeile.referenzen ?? []).map((r) => ({
            ...r,
            werte: folge.map((platz) =>
              platz.art === 'zwischen' ? (
                <Zahl
                  key={`z-${platz.z.art}-${platz.z.bis}`}
                  wert={verdichte(r.werte, platz.z, zeile.bestand)}
                  klasse="spaltensumme zwischensumme"
                />
              ) : (
                <Zahl key={platz.i} wert={r.werte[platz.i] ?? 0} spalte="konzern" zeitraum={zeitraum(bericht, platz.i)} />
              ),
            ),
          }))
        }
        waehrung={nichtUmgerechnetIn(bericht.umrechnung)}
        zeilen={bericht.zeilen}
        spaltenzahl={2 + folge.length}
        kopfzellen={folge.map((platz) => {
          if (platz.art === 'zwischen') {
            return (
              <th key={`z-${platz.z.art}-${platz.z.bis}`} className="zahl spaltensumme zwischensumme">
                {t('zwischensumme.kopf')}
                <span className="kopfvermerk">{zwischenkopf(perioden, platz.z)}</span>
              </th>
            )
          }
          const i = platz.i
          const s = bericht.spalten[i]!
          return (
          <th key={s.periode} className="zahl">
            {/* Kurz, wie im Periodenbild der Bilanz (Auftrag vom 2026-09-26). */}
            {periodenkuerzel(s.periode)}
            {/* Ob die Spalte aufgeht, sagt seit dem 2026-10-04 die
                Kontrollzeile unter ihr (R1019) und nicht mehr ihr Kopf. */}
          </th>
          )
        })}
        werte={(zeile, id) =>
          folge.map((platz) => {
            // Eine Zwischensumme trägt keinen Nachweis: Sie fasst Spalten
            // zusammen und steht in keiner Rechnung.
            if (platz.art === 'zwischen') {
              return (
                <Zahl
                  key={`z-${platz.z.art}-${platz.z.bis}`}
                  wert={verdichte(zeile.werte, platz.z, zeile.bestand)}
                  klasse="spaltensumme zwischensumme"
                />
              )
            }
            const i = platz.i
            const w = zeile.werte[i] ?? 0
            return (
            <Zahl
              key={i}
              wert={w}
              fund={istTrefferzelle(treffer, id, String(i))}
              spalte="konzern"
              zeitraum={zeitraum(bericht, i)}
              aufNachweis={nachweisruf(aufNachweis, zeile, null, null, 'konzern', zeitraum(bericht, i))}
            />
            )
          })
        }
        aufrisswerte={(z, kennung, zeile) =>
          folge.map((platz) => {
            if (platz.art === 'zwischen') {
              return (
                <Zahl
                  key={`z-${platz.z.art}-${platz.z.bis}`}
                  wert={verdichte(z.werte, platz.z, zeile.bestand)}
                  klasse="spaltensumme zwischensumme"
                />
              )
            }
            const i = platz.i
            const w = z.werte[i] ?? 0
            return (
            <Zahl
              key={i}
              wert={w}
              fund={istTrefferzelle(treffer, kennung, String(i))}
              spalte="konzern"
              zeitraum={zeitraum(bericht, i)}
              aufNachweis={nachweisruf(
                aufNachweis,
                zeile,
                z.einheit,
                z.konto,
                'konzern',
                zeitraum(bericht, i),
                z.weitere,
              )}
            />
            )
          })
        }
        aufriss={aufriss}
        quelle={quelle}
        treffer={treffer}
        herkunft={(zeile, pfad, kontext) => herkunftAusMenue(aufNachweis, zeile, pfad, kontext)}
        // **Die Probe je Spalte** (R1019). Eine Spalte, für die kein
        // Abschluss gerechnet ist, bleibt leer — sie geht nicht deshalb nicht
        // auf, weil die Rechnung falsch wäre; der Befund darunter sagt, warum.
        // Eine Zwischensumme trägt die Probe ihrer letzten Spalte: Ihr Fonds
        // am Ende ist deren.
        // **Zeile 1 klappt in die GuV auf, je Spalte die ihres Zeitraums**
        // (2.8 Abschnitt 13.33, R1105). Bis zum 2026-10-05 stand hier keine
        // Gliederung (R489, Ausnahme Periodenbild).
        gliederung={
          guvLaden
            ? () => (
                <Ergebnisgliederung<Periodenzeile>
                  zeilen={guv.bericht?.zeilen ?? null}
                  anfordern={guv.anfordern}
                  spaltenzahl={2 + folge.length}
                  vorspalte={<td className="kfrnummer" />}
                  werte={(z) => {
                    const w = guvWerte(z)
                    return folge.map((platz) =>
                      platz.art === 'zwischen' ? (
                        <Zahl
                          key={`z-${platz.z.art}-${platz.z.bis}`}
                          wert={verdichte(w, platz.z, null)}
                          klasse="spaltensumme zwischensumme"
                        />
                      ) : (
                        <Zahl key={platz.i} wert={w[platz.i] ?? 0} />
                      ),
                    )
                  }}
                />
              )
            : undefined
        }
        kontrolle={folge.map((platz) => {
          const i = platz.art === 'zwischen' ? platz.z.bis : platz.i
          const probe = spalteLeer(bericht, i)
            ? undefined
            : (bericht.abweichungen?.[i] ?? (bericht.gehtAuf[i] ? 0 : undefined))
          return platz.art === 'zwischen' ? (
            <Probezelle key={`z-${platz.z.art}-${platz.z.bis}`} wert={probe} klasse="spaltensumme zwischensumme" />
          ) : (
            <Probezelle key={i} wert={probe} />
          )
        })}
      />

      {/*
        Unter der Rechnung steht nur, was zu tun ist (R870, 2.8 Abschnitt
        13.23) — und kein Satz zu einem Monat ohne Vorperiode: Seine Spalte
        bleibt leer, und das sagt genug (2.8 Abschnitt 13.35, R1119).
      */}
      {unterDerRechnung(bericht.befunde).map((b, i) => (
        <p key={i} className="handreichung">
          {satz(b)}
        </p>
      ))}

      {/*
        **Die eine Abstimmung für den Zeitraum im Zeitschieber** (2.8
        Abschnitt 13.34, R1107–R1110) — die Summe der Spalten darüber, bei
        *kumuliert* die letzte. Sie steht immer da: ohne Sperre und ohne
        Ersatzsatz. Bis zum 2026-10-05 stand sie nur unter den
        Einzelrechnungen (13.10).
      */}
      {bericht.abstimmung && (
        <Abstimmungstabelle
          abstimmung={bericht.abstimmung}
          referenznamen={referenznamenVon(bericht.zeilen)}
          aufAenderung={aufAenderung}
        />
      )}
    </div>
  )
}

/**
 * Was unter jeder Einzelrechnung steht: die Probe, wenn sie nicht aufgeht, die
 * Befunde mit Handlungsweg (R870) und die Abstimmung.
 *
 * Sie hängt an der Rechnung und nicht am Spaltenbild — dieselben Zahlen,
 * anders sortiert, ergeben dieselbe Probe.
 */
export function Kfrnachweis({ rechnung, aufAenderung }: { rechnung: Kfrsatz; aufAenderung?: () => void }) {
  return (
    <>
      {/*
        **Unter der Rechnung steht nur, was zu tun ist** (R870, 2.8 Abschnitt
        13.23, Befund des Auftraggebers vom 2026-10-02). Hier standen drei
        Sätze, die bestätigten, was die Tabelle schon zeigt: daß die Rechnung
        aufgeht (Zeile 40), wie hoch der Deltaposten ist (seine Zeile, die es
        seit dem 2026-10-05 nicht mehr gibt, R1096) und wie die
        Stichtagskursanpassung steht (der Schalter darüber). Geblieben ist die
        Probe, wenn sie nicht aufgeht (R208), und die Hinweise mit Handlungsweg.
      */}
      {!rechnung.gehtAuf && (
        <p className="erlaeuterung fehler">
          {t('kfr.probe.differenz', {
            betrag: betrag(rechnung.abweichung),
            waehrung: rechnung.waehrung,
          })}
        </p>
      )}

      {unterDerRechnung(rechnung.befunde).map((b, i) => (
        <p key={i} className="handreichung">
          {satz(b)}
        </p>
      ))}

      <Abstimmungstabelle
        abstimmung={rechnung.abstimmung}
        referenznamen={referenznamenVon(rechnung.zeilen)}
        aufAenderung={aufAenderung}
      />
    </>
  )
}

/**
 * **Die Abstimmung gegen die Bewegungsbilanz** — unter jeder Einzelrechnung
 * und seit dem 2026-10-05 auch unter der Periodenansicht (2.8 Abschnitt 13.34,
 * R1107). Ein Bauteil für beide: Dieselbe Tabelle an zwei Stellen verschieden
 * gebaut wäre die Stelle, an der sie eines Tages verschieden rechnen.
 */
/**
 * **Wie eine Referenz in der Abstimmung heißt**: die Nummer ihrer Position
 * und ihre Bezeichnung — *„22a · Kaufpreis Tochter USA"* (2.1 Abschnitt 3.6,
 * R1714).
 */
export function referenznamenVon(
  zeilen: { code: string; referenzen?: Kfrreferenzkopf[] | null }[],
): Map<string, { code: string; name: string }> {
  const namen = new Map<string, { code: string; name: string }>()
  for (const z of zeilen) {
    for (const r of z.referenzen ?? []) {
      if (!namen.has(r.schluessel)) {
        namen.set(r.schluessel, { code: z.code, name: `${z.code} · ${referenzname(r)}` })
      }
    }
  }
  return namen
}

export function Abstimmungstabelle({
  abstimmung,
  referenznamen,
  aufAenderung,
}: {
  abstimmung: Kfrabstimmzeile[]
  /** Die Namen der Referenzen, die in der Spalte *Referenz* stehen (R1714). */
  referenznamen?: ReadonlyMap<string, { code: string; name: string }>
  /** Nach einer angelegten oder entfernten Ausnahme: die Rechnung neu holen (R1675). */
  aufAenderung?: () => void
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('kapitalflussrechnung:kfrnachweis')

  const [abstimmungOffen, setAbstimmungOffen] = useState(false)
  // **Nur Abweichungen** (2.8 Abschnitt 13.40, R1331) — bei tausend Konten
  // der Weg zu den wenigen, um die es geht.
  const [nurAbweichungen, setNurAbweichungen] = useState(false)
  // Welche Konten nach Bewegungsart aufgeklappt sind (R1329).
  const [offen, setOffen] = useState<Set<string>>(() => new Set())

  // **Die Umgliederung im Eigenkapital nur, wenn es eine gibt** (2.8
  // Abschnitt 13.33, R1104) — praktisch um den Jahreswechsel. In den übrigen
  // elf Monaten wäre sie eine leere Spalte, die keine Frage beantwortet.
  const mitUmgliederung = abstimmung.some((a) => (a.umgliederung ?? 0) !== 0)
  // **Ebenso der Effekt aus dem Konsolidierungskreis** (2.8 Abschnitt 13.49,
  // R1371): Seit die Kreisänderung eine Bewegungsart wie jede andere ist, steht
  // sie in keiner eigenen Spalte mehr — nur ein Lauf von vorher füllt sie noch.
  const mitKreis = abstimmung.some((a) => (a.konsolidierungskreis ?? 0) !== 0)
  // **Ausgenommen nur, wenn etwas ausgenommen ist** (2.8 Abschnitt 13.51,
  // R1676) — wie die Umgliederung: im Monat eines Kreiszugangs, sonst wäre es
  // eine leere Spalte. Die Ausnahmen selbst stehen immer im Block darunter.
  const mitAusgenommen = abstimmung.some((a) => (a.ausgenommen ?? 0) !== 0)
  const abweichend = abstimmung.filter((a) => a.abweichung !== 0).length
  const gezeigt = nurAbweichungen ? abstimmung.filter((a) => a.abweichung !== 0) : abstimmung
  const spalten = 8 + (mitUmgliederung ? 1 : 0) + (mitKreis ? 1 : 0) + (mitAusgenommen ? 1 : 0)
  // Die Spalten vor *Ausgenommen* — so weit reicht die Beschriftung im Block darunter.
  const vorne = 6 + (mitUmgliederung ? 1 : 0) + (mitKreis ? 1 : 0)

  function klappe(konto: string) {
    setOffen((vorher) => {
      const neu = new Set(vorher)
      if (neu.has(konto)) neu.delete(konto)
      else neu.add(konto)
      return neu
    })
  }

  // **Keine Abweichung steht als solche da** (R1331) — eine leere Zelle
  // daneben hieße auch „nicht gerechnet".
  const abweichungszelle = (wert: number) =>
    wert !== 0 ? (
      <Zahl wert={wert} klasse="delta" />
    ) : (
      <td className="zahl keineabweichung">{t('kfr.abstimmung.keine')}</td>
    )

  // **Die Zeilen, in die ein Betrag lief** (R1329). Eine Bewegung, die
  // keine Referenz zieht, sagt es — dort fehlt meist die Referenz. Was eine
  // Ausnahme nennt, steht als *ausgenommen* daneben (R1676): Steht es
  // zusätzlich in einer Zeile, sieht man beides nebeneinander.
  //
  // **Seit dem 2026-10-14 mit ihren Referenzen und dem Grund** (2.1 Abschnitt
  // 3.6, R1714): *„22a · Kaufpreis Tochter USA"* statt nur *22a*, und an einer
  // Abweichung *fehlt* oder *doppelt*. Eine Position, die über eine feste
  // Aufgabe zog, steht weiter mit ihrer Nummer da.
  const zeilenzelle = (
    zeilen: string[] | null | undefined,
    betrag: number,
    ausgenommen = 0,
    referenzen?: string[] | null,
    grund?: Abstimmgrund | null,
  ) => {
    const benannt = (referenzen ?? []).flatMap((r) => {
      const n = referenznamen?.get(r)
      return n ? [n] : []
    })
    const gedeckt = new Set(benannt.map((n) => n.code))
    const genannt = [
      ...benannt.map((n) => n.name),
      ...(zeilen ?? []).filter((z) => !gedeckt.has(z)),
      ...(ausgenommen !== 0 ? [t('kfr.abstimmung.ausgenommen.marke')] : []),
    ]
    return (
      <td className="kfrzeilen">
        {genannt.length > 0
          ? // Ein Name kann selbst Kommas tragen — „210 …, 220 …"; dann trennt das Semikolon.
            genannt.join(benannt.length > 0 ? '; ' : ', ')
          : betrag !== 0 && !grund
            ? <span className="keinezeile">{t('kfr.abstimmung.keinezeile')}</span>
            : null}
        {grund && (
          <span className="abstimmgrund" title={t(grund === 'doppelt' ? 'kfr.abstimmung.grund.doppelt.erklaerung' : 'kfr.abstimmung.grund.fehlt.erklaerung')}>
            {genannt.length > 0 ? ' · ' : ''}
            {t(grund === 'doppelt' ? 'kfr.abstimmung.grund.doppelt' : 'kfr.abstimmung.grund.fehlt')}
          </span>
        )}
      </td>
    )
  }

  // **Zwei Blöcke, Bilanz und GuV** (R1330) — die Zwischenüberschrift steht
  // nur, wo der Block Konten zeigt. **Die Konten nach ihrer Nummer**: Nach der
  // inneren Kennung stand 1576 vor 0100 und 4800 vor 4000.
  const nachNummer = (liste: Kfrabstimmzeile[]) =>
    [...liste].sort((x, y) =>
      kontobezeichnung(x.konto).localeCompare(kontobezeichnung(y.konto), 'de', { numeric: true }),
    )
  const bloecke = [
    { kopf: t('kfr.abstimmung.block.bilanz'), konten: nachNummer(gezeigt.filter((a) => !a.guv)) },
    { kopf: t('kfr.abstimmung.block.guv'), konten: nachNummer(gezeigt.filter((a) => a.guv)) },
  ].filter((b) => b.konten.length > 0)

  return (
    <>
      {/* ---- Die Abstimmung gegen die Bewegungsbilanz -------------------
          Sie steht aufklappbar unter der Rechnung und nicht in einem
          eigenen Bildschirm: Wer sie sucht, sucht sie hier — und wer sie
          nicht sucht, soll die Rechnung nicht mit ihr geteilt bekommen. */}
      <details
        className="abstimmung"
        open={abstimmungOffen}
        onToggle={(e) => setAbstimmungOffen((e.target as HTMLDetailsElement).open)}
      >
        <summary>
          {t('kfr.abstimmung.titel', { konten: anzahlwort(abstimmung.length), abweichend: anzahlwort(abweichend) })}
        </summary>
        <label className="schalter nurabweichungen">
          <input
            type="checkbox"
            checked={nurAbweichungen}
            onChange={(e) => setNurAbweichungen(e.target.checked)}
          />{' '}
          {t('kfr.abstimmung.nurabweichungen')}
        </label>
        <div className="tabellenrahmen">
          <table ref={spaltenrahmen} className="bericht ziehbaretabelle">
            <Spaltenmassleiste masse={spaltenmasse} />
            <thead>
              <tr>
                <th>{t('kfr.abstimmung.konto')}</th>
                <th className="zahl">{t('kfr.abstimmung.anfang')}</th>
                <th className="zahl">{t('kfr.abstimmung.ende')}</th>
                <th className="zahl">{t('kfr.abstimmung.bewegungsbilanz')}</th>
                {/*
                  **Ein *davon* der Bewegungsbilanz und deshalb hinter ihr**
                  (R480): Wie viel der Veränderung dieses Kontos ist Kurs und
                  kein Zahlungsstrom. Sie steht in beiden Stellungen des
                  Umschalters gleich — er entscheidet, WO der Kurseffekt in der
                  Rechnung erscheint, nicht ob es ihn gibt (R251). Eine Spalte,
                  die mit ihm auf null fiele, wäre als Prüfgröße taub.
                */}
                <th className="zahl">{t('kfr.abstimmung.stichtagskurs')}</th>
                {/*
                  **Das zweite *davon*, aus demselben Grund** (R842): Was mit
                  einer Gesellschaft in den Kreis kam oder ihn verließ, steht
                  in der Bewegungsbilanz, aber in keiner Zeile der Rechnung.
                  Ohne diese Spalte stünde jede übernommene Position als
                  Abweichung da.
                */}
                {mitKreis && <th className="zahl">{t('kfr.abstimmung.kreis')}</th>}
                {/*
                  **Das dritte *davon*** (R1104): Eine Einstellung in
                  Rücklagen steht in der Bewegungsbilanz, aber in keiner Zeile
                  (R1102). Ohne diese Spalte stünde sie jedes Jahr im Januar
                  als Abweichung an zwei Konten.
                */}
                {mitUmgliederung && <th className="zahl">{t('kfr.abstimmung.umgliederung')}</th>}
                <th className="zahl">{t('kfr.abstimmung.inrechnung')}</th>
                {/*
                  **Was keine Zeile nennen darf** (2.8 Abschnitt 13.51, R1676):
                  Steht es zusätzlich in einer Zeile, zählt es doppelt und
                  steht als Abweichung da.
                */}
                {mitAusgenommen && (
                  <th className="zahl" title={t('kfr.abstimmung.ausgenommen.zweck')}>
                    {t('kfr.abstimmung.ausgenommen')}
                  </th>
                )}
                {/* Wohin das Konto lief (R1329) — so steht an 3100 die Zeile 7. */}
                <th>{t('kfr.abstimmung.zeile')}</th>
                {/*
                  **Die Abweichung nennt das Konto, das nicht aufgeht** (2.8
                  Abschnitt 13.32, R1097). Bis zum 2026-10-05 stand davor
                  „davon Deltaposten" — den gibt es nicht mehr (R1096).
                */}
                <th className="zahl">{t('kfr.abstimmung.abweichung')}</th>
              </tr>
            </thead>
            <tbody>
              {bloecke.map((block) => (
                <Fragment key={block.kopf}>
                  <tr className="blockkopf">
                    <th colSpan={spalten}>{block.kopf}</th>
                  </tr>
                  {block.konten.map((a) => {
                    const arten = a.bewegungsarten ?? []
                    const aufgeklappt = offen.has(a.konto)
                    return (
                      <Fragment key={a.konto}>
                        <tr className={a.abweichung !== 0 ? 'mitdelta' : ''}>
                          <td>
                            {arten.length > 0 ? (
                              <button
                                type="button"
                                className="menueknopf"
                                aria-label={t('bericht.aufklappen')}
                                aria-expanded={aufgeklappt}
                                data-zeilenpfeil=""
                                onClick={() => klappe(a.konto)}
                              >
                                {aufgeklappt ? '▾' : '▸'}
                              </button>
                            ) : (
                              <Pfeilplatz />
                            )}
                            {kontobezeichnung(a.konto)}
                          </td>
                          <Zahl wert={a.anfangsbestand} />
                          <Zahl wert={a.endbestand} />
                          <Zahl wert={a.bewegungsbilanz} />
                          <Zahl wert={a.stichtagskursanpassung} />
                          {mitKreis && <Zahl wert={a.konsolidierungskreis} />}
                          {mitUmgliederung && <Zahl wert={a.umgliederung ?? 0} />}
                          <Zahl wert={a.inRechnung} />
                          {mitAusgenommen && <Zahl wert={a.ausgenommen ?? 0} />}
                          {zeilenzelle(a.zeilen, 0, a.ausgenommen ?? 0, a.referenzen, a.grund)}
                          {abweichungszelle(a.abweichung)}
                        </tr>
                        {aufgeklappt &&
                          [...arten]
                            .sort((x, y) =>
                              x.bewegungsart === null ? 1 : y.bewegungsart === null ? -1
                                : bewegungsartname(x.bewegungsart).localeCompare(bewegungsartname(y.bewegungsart), 'de', { numeric: true }),
                            )
                            .map((b) => (
                              <tr key={`${a.konto}|${b.bewegungsart ?? ''}`} className={`bewegungsartzeile ${b.abweichung !== 0 ? 'mitdelta' : ''}`.trim()}>
                                <td className="eingerueckt">
                                  <Pfeilplatz />
                                  {b.bewegungsart ? bewegungsartname(b.bewegungsart) : t('kfr.abstimmung.ohneart')}
                                </td>
                                <td />
                                <td />
                                <Zahl wert={b.bewegungsbilanz} />
                                {/* Der Teil aus der Anzeigewährung, getrennt von der gebuchten Kursdifferenz (R1680). */}
                                {b.stichtagskursanpassung ? <Zahl wert={b.stichtagskursanpassung} /> : <td />}
                                {mitKreis && <Zahl wert={b.konsolidierungskreis} />}
                                {mitUmgliederung && <Zahl wert={b.umgliederung} />}
                                <Zahl wert={b.inRechnung} />
                                {mitAusgenommen && <Zahl wert={b.ausgenommen ?? 0} />}
                                {zeilenzelle(
                                  b.zeilen,
                                  b.bewegungsbilanz - (b.stichtagskursanpassung ?? 0) - b.konsolidierungskreis - b.umgliederung - (b.ausgenommen ?? 0),
                                  b.ausgenommen ?? 0,
                                  b.referenzen,
                                  b.grund,
                                )}
                                {abweichungszelle(b.abweichung)}
                              </tr>
                            ))}
                      </Fragment>
                    )
                  })}
                </Fragment>
              ))}
              <Ausnahmenblock
                abstimmung={abstimmung}
                spalten={spalten}
                vorne={vorne}
                mitBetrag={mitAusgenommen}
                aufAenderung={aufAenderung}
              />
            </tbody>
          </table>
        </div>
      </details>

      {/* Hier standen bis zum 2026-10-04 die Umgliederungsvorschläge und die
          Umgliederungen. Die Rechnung speist sich seither ausschließlich aus
          Bewegungen (R1021, 2.8 Abschnitt 13.28). */}
    </>
  )
}

/**
 * **Von der KFR ausgenommene Bewegungen** — der dritte Block der Abstimmung,
 * unter Bilanz und GuV (2.8 Abschnitt 13.51, R1675–R1677).
 *
 * Je Ausnahme ihre Quelle und Bewegungsarten, daneben unter *Ausgenommen* ihr
 * Betrag; aufgeklappt ihre Konten. Darunter die Gegenprobe je Bewegungsart und
 * die Zeile zum Anlegen. Eine Ausnahme hat keinen Namen und nichts, was man an
 * ihr umstellen müsste: Wer sie anders will, entfernt sie und legt sie neu an.
 */
function Ausnahmenblock({
  abstimmung,
  spalten,
  vorne,
  mitBetrag,
  aufAenderung,
}: {
  abstimmung: Kfrabstimmzeile[]
  spalten: number
  vorne: number
  mitBetrag: boolean
  aufAenderung?: () => void
}) {
  const [ausnahmen, setAusnahmen] = useState<Listeneintrag[] | null>(null)
  const [offen, setOffen] = useState<Set<string>>(() => new Set())
  const [neu, setNeu] = useState<{ quelle: string; arten: string[] }>({ quelle: '', arten: [] })
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  // Die Listen zum Anlegen erst, wenn die Ausnahmen da sind — ohne sie steht der Block nicht da.
  const listen = useReferenzlisten(ausnahmen !== null)
  const darf = useDarf()

  async function lies() {
    setAusnahmen((await api.stammdatenliste('kfr_ausnahme')).eintraege)
  }

  useEffect(() => {
    // Eine Störung meldet das Störungsfenster (7.2 Abschnitt 30, R1003); die
    // Abstimmung darüber bleibt stehen.
    void lies().catch(() => undefined)
  }, [])

  if (!ausnahmen || !listen) {
    return null
  }

  async function schreibe(tun: () => Promise<unknown>) {
    setLaeuft(true)
    setMeldung(null)
    try {
      await tun()
      await lies()
      aufAenderung?.()
    } catch (fehler) {
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  function lege() {
    const absage = referenzfehler(neu.quelle, neu.arten)
    if (absage) {
      setMeldung(t(absage))
      return
    }
    const felder = Object.fromEntries(
      Object.entries({ ...quellfelder(neu.quelle), bewegungsarten: artfelder(neu.arten)['bewegungsarten'] })
        .filter(([, w]) => w !== null),
    ) as Record<string, string>
    void schreibe(async () => {
      await api.stammdatumAnlegen('kfr_ausnahme', '', { felder })
      setNeu({ quelle: '', arten: [] })
    })
  }

  const quellname = (werte: Record<string, string | undefined>) => {
    const wert = quellwert(werte)
    return listen.quellliste.find((q) => q.wert === wert)?.text ?? wert
  }
  // Nach der Nummer im Namen, wie in der Auswahl (R830): 030 vor 040.
  const artnamen = (arten: string[]) =>
    arten
      .map((a) => bewegungsartname(a))
      .sort((a, b) => a.localeCompare(b, 'de', { numeric: true }))
      .join(', ')
  // Die Ausnahmen in der Folge der Gliederung, Konten danach — wie die Referenzen einer Zeile.
  const folge = [...ausnahmen].sort(
    (a, b) =>
      Number(!!a.werte['konto']) - Number(!!b.werte['konto']) ||
      listen.stelle(a.werte['position']) - listen.stelle(b.werte['position']) ||
      (a.werte['konto'] ?? '').localeCompare(b.werte['konto'] ?? '', 'de', { numeric: true }),
  )
  const proben = gegenproben(abstimmung, [...new Set(ausnahmen.flatMap((a) => artwerte(a.werte)))])

  function klappe(schluessel: string) {
    setOffen((vorher) => {
      const naechste = new Set(vorher)
      if (naechste.has(schluessel)) naechste.delete(schluessel)
      else naechste.add(schluessel)
      return naechste
    })
  }

  return (
    <>
      <tr className="blockkopf">
        <th colSpan={spalten} title={t('kfr.ausnahmen.zweck')}>
          {t('kfr.ausnahmen.titel')}
        </th>
      </tr>
      {meldung && (
        <tr>
          <td colSpan={spalten} className="hinweis">
            {meldung}
          </td>
        </tr>
      )}
      {folge.map((a) => {
        const konten = ausnahmekonten(abstimmung, a.schluessel)
        const summe = konten.reduce((s, k) => s + k.betrag, 0)
        const aufgeklappt = offen.has(a.schluessel)
        return (
          <Fragment key={a.schluessel}>
            <tr className="ausnahme">
              <td colSpan={vorne}>
                {konten.length > 0 ? (
                  <button
                    type="button"
                    className="menueknopf"
                    aria-label={t('bericht.aufklappen')}
                    aria-expanded={aufgeklappt}
                    data-zeilenpfeil=""
                    onClick={() => klappe(a.schluessel)}
                  >
                    {aufgeklappt ? '▾' : '▸'}
                  </button>
                ) : (
                  <Pfeilplatz />
                )}
                {t('kfr.ausnahmen.zeile', { quelle: quellname(a.werte), arten: artnamen(artwerte(a.werte)) })}
              </td>
              {mitBetrag && <Zahl wert={summe} />}
              <td>
                {darf('aussondern') && (
                  <button
                    type="button"
                    className="schmal"
                    disabled={laeuft}
                    onClick={() =>
                      void schreibe(() =>
                        api.aussondern('kfr_ausnahme', a.schluessel, { weg: 'entfernen', abPeriode: null, ziel: null }),
                      )
                    }
                  >
                    {t('kfreigenschaften.entfernen')}
                  </button>
                )}
              </td>
              <td />
            </tr>
            {aufgeklappt &&
              konten.map((k) => (
                <tr key={`${a.schluessel}|${k.konto}`} className="bewegungsartzeile">
                  <td colSpan={vorne} className="eingerueckt">
                    <Pfeilplatz />
                    {kontobezeichnung(k.konto)}
                  </td>
                  {mitBetrag && <Zahl wert={k.betrag} />}
                  <td />
                  <td />
                </tr>
              ))}
          </Fragment>
        )
      })}
      {folge.length === 0 && (
        <tr>
          <td colSpan={spalten} className="hinweis">
            {t('kfr.ausnahmen.leer')}
          </td>
        </tr>
      )}
      {/* **Die Gegenprobe** (R1677): je Bewegungsart einer Ausnahme, an der
          im Zeitraum etwas steht. Geht sie nicht auf, nennt der Hinweis unter
          der Rechnung Monat und Gesellschaften. */}
      {proben.map((p) => {
        const rest = gegenprobenrest(p)
        return (
          <tr key={p.bewegungsart} className={rest !== 0 ? 'gegenprobe mitdelta' : 'gegenprobe'}>
            <td colSpan={spalten} className="erlaeuterung">
              {t(rest === 0 ? 'kfr.ausnahmen.gegenprobe.auf' : 'kfr.ausnahmen.gegenprobe.nicht', {
                bewegungsart: bewegungsartname(p.bewegungsart),
                ausgenommen: betrag(p.ausgenommen),
                fonds: betrag(p.fonds),
                rest: betrag(rest),
              })}
            </td>
          </tr>
        )
      })}
      {darf('stammdatumAnlegen') && (
        <tr className="neuzeile">
          {/* Quelle unter *Konto*, die Bewegungsarten daneben — dieselben
              beiden Felder wie bei einer Referenz, nur ohne Vorzeichen. */}
          <td>
            <Auswahlfeld
              wert={neu.quelle}
              eintraege={listen.quellliste}
              leer={t('auswahl.bitte')}
              abgeschaltet={laeuft}
              beschriftung={t('kfreigenschaften.quelle')}
              aufWahl={(w) => setNeu({ ...neu, quelle: w })}
            />
          </td>
          <td colSpan={vorne - 1 + (mitBetrag ? 1 : 0)}>
            <Bewegungsartenwahl
              werte={neu.arten}
              eintraege={listen.artliste()}
              abgeschaltet={laeuft}
              beschriftung={t('stammdaten.feld.bewegungsarten')}
              aufWahl={(arten) => setNeu({ ...neu, arten })}
            />
          </td>
          <td>
            <button type="button" disabled={laeuft} onClick={lege}>
              {t('kfr.ausnahmen.neu')}
            </button>
          </td>
          <td />
        </tr>
      )}
    </>
  )
}

/**
 * Der Umschalter für den **gesonderten Ausweis der Stichtagskursanpassung**
 * (R251) — das einzige, was vom Kopf über der Rechnung geblieben ist.
 *
 * **Er steht in der Berichtsleiste neben der Spaltenwahl** (R479, seit dem
 * 2026-09-13). Vorher trug er eine eigene Zeile, zusammen mit drei Angaben,
 * die inzwischen anderswo stehen: die Spezifikation in der Titelzeile der
 * Tabelle (R476), die Fondsabgrenzung am Bereichsband *4. Finanzmittelfonds*
 * (R478, seit dem 2026-10-05 gar nicht mehr, R1120), und der Zeitraumsatz gar
 * nicht mehr (R477). Eine ganze Zeile für
 * einen Knopf war der Befund; er zieht dorthin, wo die Bedienelemente derselben
 * Sorte stehen — die entscheiden, **wie** derselbe Lauf gelesen wird, und nicht
 * **welcher** es ist.
 *
 * **Zwei benannte Zustände und kein Haken** (R428): Beide Stellungen sind
 * richtig und beantworten verschiedene Fragen. Ein Kästchen ließe „aus" wie
 * einen Mangel aussehen.
 */
export function Kursanpassungsschalter({
  kursanpassung,
  aufKursanpassung,
}: {
  kursanpassung: boolean
  aufKursanpassung: (an: boolean) => void
}) {
  return (
    <button
      type="button"
      className={`umschalter ${kursanpassung ? 'an' : 'aus'}`}
      aria-pressed={kursanpassung}
      onClick={() => aufKursanpassung(!kursanpassung)}
      title={t(kursanpassung ? 'kfr.kursanpassung.an.hilfe' : 'kfr.kursanpassung.aus.hilfe')}
    >
      <span className="umschalterlampe" aria-hidden="true" />
      {t(kursanpassung ? 'kfr.kursanpassung.an' : 'kfr.kursanpassung.aus')}
    </button>
  )
}

export function Leer({ rechnung }: { rechnung: Kfrsatz }) {
  return (
    <div className="meldung">
      <h2>{t('kfr.leer.titel')}</h2>
      {rechnung.befunde.map((b, i) => (
        <p key={i} className="handreichung">
          {satz(b)}
        </p>
      ))}
      {rechnung.befunde.length === 0 && <p>{t('kfr.leer.grund')}</p>}
    </div>
  )
}

/**
 * Die Gesellschaften, die im Strukturbild eine Spalte bekommen.
 *
 * Aus der Rechnung selbst und nicht aus den Stammdaten: Eine Gesellschaft ohne
 * einen einzigen Beitrag bekommt keine leere Spalte.
 */
export function einheitenIn(rechnung: Kfrsatz): string[] {
  const vorhanden = new Set(
    rechnung.zeilen
      .flatMap((z) => z.beitraege)
      .map((b) => b.einheit)
      .filter(Boolean) as string[],
  )

  // Dieselben fünf Bilder, dieselbe Spaltenfolge (R167): Wer in der Bilanz
  // gelernt hat, wo sein Teilkonzern steht, soll ihn hier nicht wieder suchen.
  return nachStruktur(vorhanden, rechnung.struktur)
}

/**
 * Eine Zahl der Kapitalflussrechnung — **mit ihrem Wertenachweis**
 * (2.8 Abschnitt 13.14, R267–R270).
 *
 * Sie ist nicht die `Wert`-Zelle des Berichts, und der Grund dafür steht in
 * der Sache: Dort läuft der Nachweis über Position und Konsolidierungsspalte,
 * und eine KFR-Zeile hat keine Position. Eine zu erfinden hieße, einen
 * Nachweis zu versprechen, der eine andere Zahl erklärt als die angeklickte.
 *
 * **Ihr eigener Nachweis läuft über die KFR-Zeile selbst** und kommt aus
 * demselben Rechengang wie die Zahl darin (R267). Die Bedienung ist die
 * gleiche: Doppelklick, Eingabetaste auf der fokussierten Zelle — R213, der
 * Nachweis hängt an der Wertzelle und nicht am Bildschirm, und deshalb erben
 * ihn alle drei Spaltenbilder, statt ihn zu verdrahten.
 *
 * **Den Nachweis öffnet der Doppelklick nur auf einem Kontowert** (7.3
 * Abschnitt 27, R1032); auf der Zeile selbst und auf einer Gesellschaft ohne
 * Konto klappt er auf, wie der Pfeil davor. Der Nachweis der Zeile steht
 * weiter im Menü.
 */
export function Zahl({
  wert,
  klasse,
  fund,
  aufNachweis,
  spalte,
  zeitraum,
}: {
  wert: number
  klasse?: string
  fund?: boolean
  /**
   * Den Wertenachweis zu **dieser** Zelle öffnen — `undefined`, wo es keinen
   * gibt (Summenzeilen, R270).
   */
  aufNachweis?: Zellruf
  /** Welche Wertspalte hier steht — für das Menü, das auf der Zelle aufgeht. */
  spalte?: string
  /** Der Zeitraum der Spalte im Periodenbild — ebenfalls für das Menü (R269). */
  zeitraum?: { von: number; bis: number }
}) {
  const nachweisbar = aufNachweis !== undefined && wert !== 0
  const aufrufbar = nachweisbar && aufNachweis.kontowert
  const klappbar = nachweisbar && !aufNachweis.kontowert

  const klassen = [
    'zahl',
    klasse ?? '',
    wert < 0 ? 'minus' : '',
    fund ? 'trefferzelle' : '',
    nachweisbar ? 'nachweisbar' : '',
  ]
    .join(' ')
    .trim()

  function rufen(td: HTMLElement) {
    if (aufrufbar) {
      aufNachweis()
    } else if (klappbar) {
      zeilenpfeilBetaetigen(td)
    }
  }

  return (
    <td
      className={klassen}
      // Nicht nur mit der Maus (R143, R166): Auf der fokussierten Zelle tut es
      // die Eingabetaste. Dieselbe Bedienung wie im Bericht — wer sie dort
      // gelernt hat, braucht hier keine zweite.
      tabIndex={nachweisbar ? 0 : undefined}
      title={aufrufbar ? t('nachweis.menue') : klappbar ? t('bericht.aufklappen') : undefined}
      // Die Zelle nennt ihre Spalte und ihren Zeitraum — für das Menü, das
      // auf ihr aufgeht (R213). Ohne Nachweis eine leere Kennung, damit das
      // Menü keinen auf den Konzernwert vertauscht.
      data-spalte={nachweisbar ? spalte : ''}
      data-von={zeitraum?.von}
      data-bis={zeitraum?.bis}
      // Der Betrag, der dasteht — zum Markieren und Summieren (R1016).
      data-wert={wert}
      onDoubleClick={nachweisbar ? (e) => rufen(e.currentTarget) : undefined}
      // Der zweite Klick eines Doppelklicks markiert sonst die Zahl.
      onMouseDown={(e) => {
        if (nachweisbar && e.detail > 1) {
          e.preventDefault()
        }
      }}
      onKeyDown={(e) => {
        if (nachweisbar && e.key === 'Enter') {
          e.preventDefault()
          rufen(e.currentTarget)
        }
      }}
    >
      <Betrag wert={wert} leerBeiNull />
    </td>
  )
}

/**
 * Eine Zelle der Kontrollzeile (2.8 Abschnitt 13.26, R1019).
 *
 * **Das grüne Häkchen, wo die Spalte aufgeht** — Zeile 40 trifft den Bestand
 * der Fondskonten auf den Cent. Sonst die Differenz, rot. Leer, wo die Spalte
 * Zeile 40 gar nicht zeigt oder nichts gerechnet ist: Ein Häkchen unter einer
 * leeren Spalte bestätigte etwas, das niemand sieht.
 */
export function Probezelle({ wert, klasse }: { wert: number | undefined; klasse?: string }) {
  if (wert === undefined) {
    return <td className={['zahl', klasse].filter(Boolean).join(' ')} />
  }

  const stimmt = Math.abs(wert) < 0.005
  return (
    <td
      className={['zahl', 'probe', stimmt ? 'stimmt' : 'minus', klasse].filter(Boolean).join(' ')}
      title={t(stimmt ? 'kfr.kontrolle.stimmt' : 'kfr.kontrolle.differenz')}
    >
      {stimmt ? <Zustandssymbol art="fertig" /> : <Betrag wert={wert} />}
    </td>
  )
}

/** Mehrere Zeilen ohne umschließendes Element — `<tbody>` duldet kein `<div>`. */
function Fragmentzeile({ children }: { children: React.ReactNode }) {
  return <>{children}</>
}

/**
 * Der Zeitraum, für den eine Spalte des Periodenbildes steht.
 *
 * **Jede Spalte ist eine eigene Rechnung** mit eigenem Anfangsbestand — und
 * deshalb muss der Nachweis daraus wissen, wo sie anfängt. Ohne ihn erklärte
 * ein Klick auf das zweite Quartal das ganze Jahr (R269).
 */
function zeitraum(bericht: Kfrperiodenbericht, spalte: number): { von: number; bis: number } {
  return { von: bericht.von[spalte]!, bis: bericht.spalten[spalte]!.periode }
}

/** Trägt diese Spalte des Periodenbildes überhaupt eine Zahl? */
function spalteLeer(bericht: Kfrperiodenbericht, spalte: number): boolean {
  return bericht.zeilen.every((z) => (z.werte[spalte] ?? 0) === 0)
}
